import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiPlus, FiSearch, FiFileText, FiEdit3, FiRefreshCw, FiCalendar, FiCheck, FiBriefcase,
  FiList, FiExternalLink, FiEye, FiDownload, FiPrinter, FiX
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { proformaAPI, documentAPI, companyAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { generateProformaInvoicePDF } from '../PDF/PDFGenerator';

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return '';
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmt = (v) =>
  v != null && v !== ''
    ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '';

const todayISO = () => new Date().toISOString().slice(0, 10);

const firstDayOfFY = () => {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${year}-04-01`;
};

export default function ProformaList() {
  const navigate = useNavigate();
  const { activeCompany, companies } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectingCompany, setSelectingCompany] = useState(!activeCompany);
  const [fromDate, setFromDate] = useState(firstDayOfFY());
  const [toDate, setToDate] = useState(todayISO());
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [expandedProformaId, setExpandedProformaId] = useState(null);
  const [linkedInvoices, setLinkedInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const pdfIframeRef = useRef(null);

  // ── Proforma PDF preview / download / print ─────────────────────────────
  const handlePdfPreview = async (docId) => {
    setGeneratingId(docId);
    try {
      const docRes = await proformaAPI.getById(docId);
      const fullDoc = docRes.data;

      const companyId = fullDoc.company_id || activeCompany?.id;
      const compRes = await companyAPI.getById(companyId);
      const company = compRes.data;

      // Find primary active authority for the signature block
      const authority = (company.authorities || []).find(
        (a) => a.is_primary && a.status === 'Active'
      ) || (company.authorities || [])[0] || null;

      const pdfBlob = generateProformaInvoicePDF(fullDoc, company, authority, true);
      setPdfPreview({ doc: fullDoc, url: URL.createObjectURL(pdfBlob) });
    } catch (err) {
      console.error('Proforma PDF error:', err);
      toast.error(err.response?.data?.error || err.message || 'Failed to generate PDF');
    } finally {
      setGeneratingId(null);
    }
  };

  const closePdfPreview = () => {
    if (pdfPreview?.url) URL.revokeObjectURL(pdfPreview.url);
    setPdfPreview(null);
  };

  const handleDownloadPdf = () => {
    if (!pdfPreview) return;
    const link = document.createElement('a');
    link.href = pdfPreview.url;
    link.download = `Proforma_${(pdfPreview.doc.invoice_no || 'Document').replace(/[\\/\s]+/g, '_')}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('PDF downloaded successfully!');
  };

  const handlePrintPdf = () => {
    if (!pdfPreview) return;
    try {
      const win = pdfIframeRef.current?.contentWindow;
      if (win) {
        win.focus();
        win.print();
        return;
      }
    } catch {
      // fall through to opening in a new tab
    }
    window.open(pdfPreview.url, '_blank');
  };

  const toggleInvoices = async (proformaId) => {
    if (expandedProformaId === proformaId) {
      setExpandedProformaId(null);
      setLinkedInvoices([]);
      return;
    }
    setExpandedProformaId(proformaId);
    setLoadingInvoices(true);
    try {
      const res = await documentAPI.getAll({ proforma_id: proformaId });
      setLinkedInvoices(Array.isArray(res.data) ? res.data : []);
    } catch {
      setLinkedInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const fetchDocuments = async () => {
    if (!activeCompany) return;
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (selectedCompanyId) params.company_id = selectedCompanyId;

      const res = await proformaAPI.getAll(params);
      setDocuments(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeCompany) {
      setSelectingCompany(true);
    } else {
      setSelectingCompany(false);
      fetchDocuments();
    }
  }, [activeCompany, fromDate, toDate, selectedCompanyId]);

  const filteredDocs = documents.filter((doc) =>
    searchTerm
      ? doc.invoice_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.buyer_name?.toLowerCase().includes(searchTerm.toLowerCase())
      : true
  );

  const styles = {
    page: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      maxWidth: 1400,
      margin: '0 auto',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 24,
      flexWrap: 'wrap',
      gap: 12,
    },
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 14,
    },
    titleIcon: {
      width: 48,
      height: 48,
      borderRadius: 14,
      background: 'linear-gradient(135deg, #10b981, #059669)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)',
    },
    title: {
      fontSize: 28,
      fontWeight: 800,
      color: '#064e3b',
      margin: 0,
      fontFamily: "'Poppins', sans-serif",
    },
    subtitle: {
      fontSize: 13,
      color: '#6b7280',
      marginTop: 2,
    },
    actionRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flexWrap: 'wrap',
    },
    btnPrimary: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '10px 20px',
      background: 'linear-gradient(135deg, #10b981, #059669)',
      color: '#fff',
      border: 'none',
      borderRadius: 12,
      fontSize: 14,
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: "'Inter', sans-serif",
      boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)',
      transition: 'all 0.2s ease',
    },
    btnSecondary: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '10px 16px',
      background: '#fff',
      color: '#059669',
      border: '2px solid rgba(16,185,129,0.2)',
      borderRadius: 12,
      fontSize: 14,
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: "'Inter', sans-serif",
      transition: 'all 0.2s ease',
    },
    searchBox: {
      position: 'relative',
      flexGrow: 1,
      maxWidth: 400,
    },
    searchInput: {
      width: '100%',
      padding: '10px 16px 10px 44px',
      border: '2px solid rgba(16,185,129,0.15)',
      borderRadius: 12,
      fontSize: 14,
      outline: 'none',
      fontFamily: "'Inter', sans-serif",
      color: '#1e293b',
      background: '#fff',
      transition: 'border-color 0.15s ease',
      boxSizing: 'border-box',
    },
    searchIcon: {
      position: 'absolute',
      left: 16,
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#94a3b8',
    },
    filterRow: {
      display: 'flex',
      gap: 16,
      marginBottom: 20,
      flexWrap: 'wrap',
      alignItems: 'flex-end',
    },
    filterGroup: {
      flex: 1,
      minWidth: 140,
    },
    filterLabel: {
      display: 'block',
      fontSize: 12,
      fontWeight: 600,
      color: '#047857',
      marginBottom: 6,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    filterInput: {
      width: '100%',
      padding: '10px 14px',
      border: '2px solid rgba(16,185,129,0.2)',
      borderRadius: 10,
      fontSize: 14,
      outline: 'none',
      fontFamily: "'Inter', sans-serif",
      color: '#1e293b',
      background: '#fff',
      transition: 'all 0.15s ease',
      boxSizing: 'border-box',
    },
    card: {
      background: 'rgba(255, 255, 255, 0.85)',
      backdropFilter: 'blur(16px)',
      borderRadius: 20,
      padding: 24,
      boxShadow: '0 4px 20px rgba(6, 78, 59, 0.06)',
      border: '1px solid rgba(16,185,129,0.12)',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: 16,
    },
    th: {
      padding: '12px 16px',
      background: 'linear-gradient(135deg, #064e3b, #047857)',
      color: '#fff',
      fontSize: 12,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      textAlign: 'left',
    },
    td: {
      padding: '14px 16px',
      borderBottom: '1px solid rgba(16,185,129,0.08)',
      fontSize: 14,
      color: '#1e293b',
    },
    statusBadge: (status) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '4px 10px',
      borderRadius: 8,
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      background:
        status === 'finalized'
          ? 'rgba(16, 185, 129, 0.15)'
          : status === 'saved'
          ? 'rgba(59, 130, 246, 0.15)'
          : 'rgba(251, 191, 36, 0.15)',
      color:
        status === 'finalized'
          ? '#059669'
          : status === 'saved'
          ? '#2563eb'
          : '#d97706',
      border: `1px solid ${
        status === 'finalized'
          ? 'rgba(16, 185, 129, 0.3)'
          : status === 'saved'
          ? 'rgba(59, 130, 246, 0.3)'
          : 'rgba(251, 191, 36, 0.3)'
      }`,
    }),
    loadingOverlay: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 300,
      gap: 16,
    },
    spinner: {
      width: 40,
      height: 40,
      border: '4px solid rgba(16, 185, 129, 0.2)',
      borderTopColor: '#10b981',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#9ca3af',
    },
    actionBtn: {
      background: 'none',
      border: 'none',
      color: '#059669',
      cursor: 'pointer',
      padding: '6px 10px',
      borderRadius: 8,
      fontSize: 13,
      fontWeight: 600,
      transition: 'background 0.15s ease',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
    },
    companySelector: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #ecfdf5, #f0fdf4)',
      padding: 20,
    },
    companySelectorCard: {
      background: '#fff',
      borderRadius: 24,
      padding: 48,
      boxShadow: '0 20px 60px rgba(6, 78, 59, 0.1)',
      maxWidth: 600,
      width: '100%',
      border: '1px solid rgba(16,185,129,0.15)',
    },
    companySelectorTitle: {
      fontSize: 32,
      fontWeight: 800,
      color: '#064e3b',
      textAlign: 'center',
      marginBottom: 12,
      fontFamily: "'Poppins', sans-serif",
    },
    companySelectorSubtitle: {
      fontSize: 15,
      color: '#6b7280',
      textAlign: 'center',
      marginBottom: 40,
    },
    companyGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: 16,
    },
    companyOption: {
      padding: 20,
      border: '2px solid rgba(16,185,129,0.2)',
      borderRadius: 16,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      background: '#fff',
    },
    companyOptionHover: {
      borderColor: '#10b981',
      background: '#f0fdf4',
      boxShadow: '0 8px 24px rgba(16, 185, 129, 0.15)',
    },
    companyOptionName: {
      fontSize: 16,
      fontWeight: 700,
      color: '#064e3b',
      marginBottom: 6,
    },
    companyOptionDetails: {
      fontSize: 13,
      color: '#6b7280',
      marginBottom: 4,
    },
  };

  // Company selection screen
  if (selectingCompany) {
    return (
      <div style={styles.companySelector}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={styles.companySelectorCard}
        >
          <h1 style={styles.companySelectorTitle}>Select Company</h1>
          <p style={styles.companySelectorSubtitle}>
            Choose a company to view and manage proforma invoices
          </p>

          {companies && companies.length > 0 ? (
            <div style={styles.companyGrid}>
              {companies.map((company) => (
                <motion.div
                  key={company.id}
                  whileHover={{ y: -4 }}
                  onClick={() => {
                    // Store selected company and close selector
                    localStorage.setItem('selectedCompanyId', company.id);
                    setSelectingCompany(false);
                    // Trigger company switch via auth context if available
                    window.location.reload();
                  }}
                  style={styles.companyOption}
                  onMouseEnter={(e) => {
                    Object.assign(e.currentTarget.style, styles.companyOptionHover);
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(16,185,129,0.2)';
                    e.currentTarget.style.background = '#fff';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={styles.companyOptionName}>{company.company_name}</div>
                  <div style={styles.companyOptionDetails}>
                    {company.exporter_name && <div>📦 {company.exporter_name}</div>}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: '#10b981',
                      marginTop: 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <FiCheck size={14} /> Select this company
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#9ca3af' }}>
              <p>No companies found. Please contact support.</p>
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      style={styles.page}
    >
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.titleRow}>
          <div style={styles.titleIcon}>
            <FiFileText size={24} color="#fff" />
          </div>
          <div>
            <h1 style={styles.title}>Proforma Invoices</h1>
            <p style={styles.subtitle}>
              {activeCompany?.exporter_name || 'No company selected'}
            </p>
          </div>
        </div>

        <div style={styles.actionRow}>
          <div style={styles.searchBox}>
            <FiSearch size={18} style={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search by invoice no or buyer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
              onFocus={(e) => {
                e.target.style.borderColor = '#10b981';
                e.target.style.boxShadow = '0 0 0 3px rgba(16,185,129,0.12)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(16,185,129,0.15)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={fetchDocuments}
            style={styles.btnSecondary}
          >
            <FiRefreshCw size={16} /> Refresh
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/proforma-invoices/new')}
            style={styles.btnPrimary}
          >
            <FiPlus size={18} /> New Proforma
          </motion.button>
        </div>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        style={styles.filterRow}
      >
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>From Date</label>
          <div style={{ position: 'relative' }}>
            <FiCalendar
              size={15}
              style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#059669' }}
            />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{ ...styles.filterInput, paddingLeft: 34 }}
              onFocus={(e) => {
                e.target.style.borderColor = '#10b981';
                e.target.style.boxShadow = '0 0 0 3px rgba(16,185,129,0.15)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(16,185,129,0.2)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>To Date</label>
          <div style={{ position: 'relative' }}>
            <FiCalendar
              size={15}
              style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#059669' }}
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={{ ...styles.filterInput, paddingLeft: 34 }}
              onFocus={(e) => {
                e.target.style.borderColor = '#10b981';
                e.target.style.boxShadow = '0 0 0 3px rgba(16,185,129,0.15)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(16,185,129,0.2)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>
      </motion.div>

      {/* Document List */}
      <div style={styles.card}>
        {loading ? (
          <div style={styles.loadingOverlay}>
            <div style={styles.spinner} />
            <span style={{ color: '#6b7280', fontSize: 14 }}>Loading proforma invoices...</span>
          </div>
        ) : filteredDocs.length === 0 ? (
          <div style={styles.emptyState}>
            <FiFileText size={48} style={{ marginBottom: 16, color: 'rgba(16,185,129,0.3)' }} />
            <h3 style={{ color: '#6b7280', fontWeight: 600, marginBottom: 8 }}>
              {searchTerm ? 'No matching proforma invoices' : 'No proforma invoices yet'}
            </h3>
            <p style={{ color: '#9ca3af', fontSize: 14 }}>
              {searchTerm ? 'Try a different search term' : 'Click "New Proforma" to create your first proforma invoice.'}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, borderTopLeftRadius: 12 }}>Invoice No</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Buyer</th>
                  <th style={styles.th}>Country</th>
                  <th style={styles.th}>Amount (USD)</th>
                  <th style={styles.th}>Status</th>
                  <th style={{ ...styles.th, borderTopRightRadius: 12, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {filteredDocs.map((doc, idx) => (
                    <React.Fragment key={doc.id}>
                      <motion.tr
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.03, duration: 0.2 }}
                        style={{ background: idx % 2 === 0 ? '#fff' : '#f8fdfb' }}
                      >
                        <td style={{ ...styles.td, fontWeight: 600, color: '#059669' }}>
                          {doc.invoice_no || '--'}
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FiCalendar size={14} color="#94a3b8" />
                            {fmtDate(doc.document_date) || fmtDate(doc.created_at) || '--'}
                          </div>
                        </td>
                        <td style={styles.td}>{doc.buyer_name || '--'}</td>
                        <td style={styles.td}>{doc.country_of_origin || 'INDIA'}</td>
                        <td style={{ ...styles.td, fontWeight: 600, color: '#064e3b' }}>
                          ${fmt(doc.total_usd)}
                        </td>
                        <td style={styles.td}>
                          <span style={styles.statusBadge(doc.status)}>
                            {doc.status || 'draft'}
                          </span>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                            <button
                              onClick={() => handlePdfPreview(doc.id)}
                              disabled={generatingId === doc.id}
                              style={{
                                ...styles.actionBtn,
                                color: '#0d9488',
                                opacity: generatingId === doc.id ? 0.6 : 1,
                                cursor: generatingId === doc.id ? 'wait' : 'pointer',
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#f0fdfa')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                              title="View Proforma Invoice PDF"
                            >
                              {generatingId === doc.id ? (
                                <span style={{
                                  width: 14, height: 14, display: 'inline-block',
                                  border: '2px solid rgba(13,148,136,0.25)', borderTopColor: '#0d9488',
                                  borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                                }} />
                              ) : (
                                <FiEye size={15} />
                              )}{' '}
                              View
                            </button>
                            <button
                              onClick={() => navigate(`/proforma-invoices/${doc.id}`)}
                              style={styles.actionBtn}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#ecfdf5')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                            >
                              <FiEdit3 size={15} /> Edit
                            </button>
                            <button
                              onClick={() => navigate(`/commercial-invoices/new?proformaId=${doc.id}`)}
                              style={{ ...styles.actionBtn, color: '#2563eb' }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#eff6ff')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                              title="Create Commercial Invoice from this Proforma"
                            >
                              <FiPlus size={15} /> Invoice
                            </button>
                            <button
                              onClick={() => toggleInvoices(doc.id)}
                              style={{
                                ...styles.actionBtn,
                                color: '#7c3aed',
                                background: expandedProformaId === doc.id ? '#f3e8ff' : 'none',
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#f3e8ff')}
                              onMouseLeave={(e) => {
                                if (expandedProformaId !== doc.id) e.currentTarget.style.background = 'none';
                              }}
                              title="Show linked Commercial Invoices"
                            >
                              <FiList size={15} /> History
                            </button>
                          </div>
                        </td>
                      </motion.tr>

                      {/* Expandable linked invoices */}
                      {expandedProformaId === doc.id && (
                        <motion.tr
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <td colSpan={7} style={{ padding: 0, background: '#faf5ff', borderBottom: '2px solid rgba(124, 58, 237, 0.15)' }}>
                            <div style={{ padding: '16px 24px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <FiList size={16} color="#7c3aed" />
                                <span style={{ fontWeight: 700, fontSize: 13, color: '#4c1d95', fontFamily: "'Poppins', sans-serif" }}>
                                  Commercial Invoices from {doc.invoice_no}
                                </span>
                              </div>

                              {loadingInvoices ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0' }}>
                                  <div style={{
                                    width: 18, height: 18, border: '2px solid #e9d5ff',
                                    borderTopColor: '#7c3aed', borderRadius: '50%',
                                    animation: 'spin 0.8s linear infinite',
                                  }} />
                                  <span style={{ color: '#6b7280', fontSize: 13 }}>Loading invoices...</span>
                                </div>
                              ) : linkedInvoices.length === 0 ? (
                                <p style={{ color: '#6b7280', fontSize: 13, margin: 0, padding: '8px 0' }}>
                                  No commercial invoices created from this proforma yet.
                                </p>
                              ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr>
                                      {['Invoice No', 'Date', 'Buyer', 'Amount (USD)', 'Status', ''].map((h, i) => (
                                        <th key={i} style={{
                                          padding: '8px 12px',
                                          textAlign: i === 3 ? 'right' : i === 5 ? 'center' : 'left',
                                          fontSize: 11, fontWeight: 700, color: '#6d28d9',
                                          textTransform: 'uppercase', letterSpacing: 0.5,
                                          borderBottom: '1px solid rgba(124,58,237,0.15)',
                                          width: i === 5 ? 50 : undefined,
                                        }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {linkedInvoices.map((inv) => (
                                      <tr
                                        key={inv.id}
                                        style={{ transition: 'background 0.15s ease', cursor: 'pointer' }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.6)'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                        onClick={() => navigate(`/commercial-invoices/edit/${inv.id}`)}
                                      >
                                        <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 600, color: '#4c1d95' }}>
                                          {inv.invoice_no}
                                        </td>
                                        <td style={{ padding: '10px 12px', fontSize: 13, color: '#374151' }}>
                                          {fmtDate(inv.document_date) || '-'}
                                        </td>
                                        <td style={{ padding: '10px 12px', fontSize: 13, color: '#374151' }}>
                                          {inv.buyer_name || '-'}
                                        </td>
                                        <td style={{ padding: '10px 12px', fontSize: 13, color: '#374151', textAlign: 'right', fontWeight: 600 }}>
                                          {inv.total_usd != null ? `$${fmt(inv.total_usd)}` : '-'}
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                          <span style={{
                                            display: 'inline-block', padding: '2px 10px', borderRadius: 9999,
                                            fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
                                            background: inv.status === 'finalized' ? '#dbeafe' : inv.status === 'saved' ? '#ecfdf5' : '#fef9c3',
                                            color: inv.status === 'finalized' ? '#1d4ed8' : inv.status === 'saved' ? '#059669' : '#a16207',
                                            border: `1px solid ${inv.status === 'finalized' ? 'rgba(29,78,216,0.2)' : inv.status === 'saved' ? 'rgba(16,185,129,0.2)' : 'rgba(161,98,7,0.2)'}`,
                                          }}>
                                            {inv.status || 'draft'}
                                          </span>
                                        </td>
                                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                          <FiExternalLink size={14} color="#7c3aed" />
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </motion.tr>
                      )}
                    </React.Fragment>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* PDF Preview Modal */}
      <AnimatePresence>
        {pdfPreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              padding: 20,
            }}
            onClick={closePdfPreview}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 30 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              style={{
                background: 'linear-gradient(to bottom, #ffffff, #fafafa)',
                borderRadius: 16,
                maxWidth: '95vw',
                width: 1100,
                maxHeight: '92vh',
                height: '90vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.08)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                style={{
                  background: 'linear-gradient(135deg, #047857 0%, #059669 100%)',
                  padding: '16px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                    }}
                  >
                    <FiFileText size={22} color="#ffffff" />
                  </div>
                  <div>
                    <h3
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: 19,
                        fontWeight: 700,
                        color: '#ffffff',
                        margin: 0,
                        marginBottom: 2,
                      }}
                    >
                      {pdfPreview.doc.invoice_no || 'Proforma Invoice'}
                    </h3>
                    <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                      Proforma Invoice
                      {pdfPreview.doc.buyer_name && ` • ${pdfPreview.doc.buyer_name}`}
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  {/* Print */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handlePrintPdf}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 18px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 10,
                      color: '#ffffff',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: "'Inter', sans-serif",
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
                    title="Print PDF"
                  >
                    <FiPrinter size={16} /> Print
                  </motion.button>

                  {/* Download */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleDownloadPdf}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 18px',
                      background: '#ffffff',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      borderRadius: 10,
                      color: '#047857',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: "'Inter', sans-serif",
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
                    title="Download PDF"
                  >
                    <FiDownload size={16} /> Download
                  </motion.button>

                  {/* Close */}
                  <button
                    onClick={closePdfPreview}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.15)',
                      color: '#ffffff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(239, 68, 68, 0.9)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)')}
                    title="Close"
                  >
                    <FiX size={20} />
                  </button>
                </div>
              </div>

              {/* PDF Viewer */}
              <div style={{ flex: 1, overflow: 'hidden', background: '#e5e7eb' }}>
                <iframe
                  ref={pdfIframeRef}
                  src={`${pdfPreview.url}#toolbar=1&navpanes=0&scrollbar=1&view=FitH`}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="Proforma Invoice PDF Preview"
                />
              </div>

              {/* Footer */}
              <div
                style={{
                  padding: '10px 24px',
                  background: '#f9fafb',
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: 12,
                  color: '#6b7280',
                }}
              >
                <span style={{ fontWeight: 500 }}>
                  📅 {fmtDate(pdfPreview.doc.document_date) || fmtDate(new Date())}
                </span>
                <span>💼 {activeCompany?.exporter_name || 'DEY INTL.'}</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </motion.div>
  );
}
